import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Activity, X, Cpu, HardDrive, Database, AlertTriangle, Layers, RefreshCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES_PERMITIDOS = ['administrador', 'coordinador'];
const INTERVALO_MS = 5000;

// ---- helpers de formato ----
const fmtBytes = (b) => {
  if (b === null || b === undefined) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtNum = (n, d = 0) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('es-CO', { maximumFractionDigits: d }));

// ---- estilos por status ----
const colorStatus = {
  ok: 'text-emerald-600',
  warn: 'text-amber-500',
  crit: 'text-red-600',
};
const bgBarra = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  crit: 'bg-red-600',
};
const dotStatus = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  crit: 'bg-red-600',
};

function Barra({ porcentaje, status }) {
  const pct = Math.max(0, Math.min(100, porcentaje || 0));
  return (
    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
      <div className={`h-full ${bgBarra[status] || 'bg-slate-400'} transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function Fila({ label, valor, status }) {
  return (
    <div className="flex items-center justify-between text-sm py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium tabular-nums ${status ? colorStatus[status] : 'text-slate-700'}`}>{valor}</span>
    </div>
  );
}

function Seccion({ icon: Icon, titulo, status, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-700 flex-1">{titulo}</h3>
        {status && <span className={`w-2.5 h-2.5 rounded-full ${dotStatus[status]}`} />}
      </div>
      {children}
    </div>
  );
}

export default function ServerMonitor() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [cols, setCols] = useState(null);
  const [colsLoading, setColsLoading] = useState(false);
  const intervalRef = useRef(null);

  const esVisible = user && ROLES_PERMITIDOS.includes(user.role);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/server-metrics`);
      setData(res.data);
      setError(false);
      setUltimaActualizacion(new Date());
    } catch (e) {
      setError(true); // conserva últimos valores
    }
  }, []);

  useEffect(() => {
    if (!open || !esVisible) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }
    fetchMetrics();
    intervalRef.current = setInterval(fetchMetrics, INTERVALO_MS);
    return () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
  }, [open, esVisible, fetchMetrics]);

  const analizarColecciones = async () => {
    setColsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/server-metrics/colecciones`);
      setCols(res.data.colecciones || []);
    } catch (e) {
      setCols([]);
    } finally {
      setColsLoading(false);
    }
  };

  if (!esVisible) return null;

  const overall = data?.overall_status || 'ok';
  const host = data?.host || {};
  const mongo = data?.mongo || {};

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setOpen((v) => !v)}
        title="Monitor del servidor"
        className={`fixed bottom-5 right-5 z-[60] w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white transition-colors
          ${overall === 'crit' ? 'bg-red-600 hover:bg-red-700 animate-pulse' : overall === 'warn' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-700 hover:bg-emerald-800'}`}
      >
        <Activity className="w-6 h-6" />
      </button>

      {/* Overlay */}
      {open && <div className="fixed inset-0 bg-black/20 z-[60]" onClick={() => setOpen(false)} />}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[380px] max-w-[90vw] bg-slate-50 shadow-2xl z-[70] flex flex-col transition-transform duration-300
          ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-white">
          <Activity className={`w-5 h-5 ${colorStatus[overall]}`} />
          <h2 className="font-semibold text-slate-800 flex-1">Monitor del servidor</h2>
          <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-4 py-1.5 text-xs text-slate-400 flex items-center justify-between border-b border-slate-100 bg-white">
          <span>{error ? '⚠ Desconectado, reintentando…' : 'Actualiza cada 5 s'}</span>
          <span>{ultimaActualizacion ? ultimaActualizacion.toLocaleTimeString('es-CO') : '…'}</span>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {!data ? (
            <div className="flex items-center justify-center h-32 text-slate-400 text-sm">Cargando métricas…</div>
          ) : (
            <>
              {/* HOST */}
              <Seccion icon={Cpu} titulo="Host" status={_peorHost(host)}>
                {host.error ? (
                  <p className="text-xs text-red-500">{host.error}</p>
                ) : (
                  <>
                    <Fila label="RAM disponible" valor={fmtBytes(host.ram?.disponible_bytes)} status={host.ram?.status} />
                    <Barra porcentaje={host.ram?.porcentaje} status={host.ram?.status} />
                    <div className="text-[11px] text-slate-400 mb-2">{fmtBytes(host.ram?.usada_bytes)} / {fmtBytes(host.ram?.total_bytes)} usada</div>
                    <Fila label="Swap usada" valor={`${fmtBytes(host.swap?.usada_bytes)} (${host.swap?.porcentaje ?? '—'}%)`} status={host.swap?.status} />
                    <Fila label="CPU" valor={`${host.cpu?.porcentaje ?? '—'}% · ${host.cpu?.nucleos ?? '?'} núcleos`} status={host.cpu?.status} />
                    <Fila label="Carga (1m)" valor={host.load?.avg1 != null ? host.load.avg1.toFixed(2) : '—'} status={host.load?.status} />
                    <Fila label="Disco /" valor={`${host.disco?.porcentaje ?? '—'}%`} status={host.disco?.status} />
                    <Barra porcentaje={host.disco?.porcentaje} status={host.disco?.status} />
                  </>
                )}
              </Seccion>

              {/* MONGODB */}
              <Seccion icon={Database} titulo="MongoDB" status={_peorMongo(mongo)}>
                {mongo.error ? (
                  <p className="text-xs text-red-500">{mongo.error}</p>
                ) : (
                  <>
                    <Fila label="Caché WiredTiger" valor={`${mongo.cache?.porcentaje ?? '—'}%`} status={mongo.cache?.status} />
                    <Barra porcentaje={mongo.cache?.porcentaje} status={mongo.cache?.status} />
                    <div className="text-[11px] text-slate-400 mb-2">{fmtBytes(mongo.cache?.usada_bytes)} / {fmtBytes(mongo.cache?.max_bytes)}</div>
                    <Fila label="Thrashing (expuls./s)" valor={fmtNum(mongo.thrashing?.paginas_expulsadas_s, 0)} status={mongo.thrashing?.status} />
                    <Fila label="Lecturas a caché/s" valor={fmtNum(mongo.thrashing?.paginas_leidas_s, 0)} />
                    <Fila label="Queries/s" valor={fmtNum(mongo.throughput?.queries_s, 1)} />
                    <Fila label="Ops/s" valor={fmtNum(mongo.throughput?.ops_s, 1)} />
                    <Fila label="Conexiones" valor={`${mongo.conexiones?.actual ?? '—'} (act. ${mongo.conexiones?.activas ?? '—'})`} />
                    <Fila label="En cola (L/E)" valor={`${mongo.cola?.lectores ?? 0} / ${mongo.cola?.escritores ?? 0}`} />
                  </>
                )}
              </Seccion>

              {/* QUERIES LENTAS */}
              <Seccion icon={AlertTriangle} titulo={`Queries lentas (${data.slow_ops?.length || 0})`} status={data.slow_ops?.length ? _peorSlow(data.slow_ops) : 'ok'}>
                {!data.slow_ops?.length ? (
                  <p className="text-xs text-slate-400">Sin operaciones lentas (&ge; 2 s).</p>
                ) : (
                  <div className="space-y-2">
                    {data.slow_ops.map((op, i) => (
                      <div key={i} className="text-xs border-l-2 pl-2 border-slate-300">
                        <div className={`font-medium ${colorStatus[op.status]}`}>{op.secs}s · {op.op}</div>
                        <div className="text-slate-500 truncate">{op.ns}</div>
                        {op.plan && <div className="text-slate-400 truncate">plan: {op.plan}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </Seccion>

              {/* COLECCIONES (a demanda) */}
              <Seccion icon={Layers} titulo="Colecciones por tamaño">
                {!cols ? (
                  <button onClick={analizarColecciones} disabled={colsLoading}
                    className="w-full text-sm py-1.5 rounded border border-slate-300 hover:bg-slate-100 flex items-center justify-center gap-2 disabled:opacity-50">
                    <RefreshCcw className={`w-4 h-4 ${colsLoading ? 'animate-spin' : ''}`} />
                    {colsLoading ? 'Analizando…' : 'Analizar colecciones'}
                  </button>
                ) : (
                  <div className="space-y-1">
                    {cols.map((c) => (
                      <div key={c.nombre} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600 truncate mr-2">{c.nombre}</span>
                        <span className="tabular-nums text-slate-500 whitespace-nowrap">
                          {fmtBytes(c.storage_bytes)} · {fmtNum(c.docs)} docs · {fmtBytes(c.avg_obj_bytes)}/doc
                        </span>
                      </div>
                    ))}
                    <button onClick={analizarColecciones} disabled={colsLoading}
                      className="w-full text-xs py-1 mt-1 text-slate-400 hover:text-slate-600">
                      {colsLoading ? 'Analizando…' : 'Refrescar'}
                    </button>
                  </div>
                )}
              </Seccion>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// peor status entre las subsecciones (para el punto de la sección)
const _orden = { ok: 0, warn: 1, crit: 2 };
function _peor(arr) {
  return arr.reduce((p, s) => (_orden[s] > _orden[p] ? s : p), 'ok');
}
function _peorHost(h) {
  if (h.error) return 'crit';
  return _peor([h.ram?.status, h.swap?.status, h.cpu?.status, h.load?.status, h.disco?.status].filter(Boolean));
}
function _peorMongo(m) {
  if (m.error) return 'crit';
  return _peor([m.cache?.status, m.thrashing?.status].filter(Boolean));
}
function _peorSlow(ops) {
  return _peor(ops.map((o) => o.status));
}
